<template>
  <div v-if="applicationList.length > 0" class="candidate-info-card-list">
    <CandidateInfoCard
      v-for="(application) in applicationList"
      :key="application.id"
      :rowData="application"
      :showDuplicateCandidateIcon="hasDuplicateCandidatePermission"
      :showAssessmentFit="hasAssessmentPermission"
      :showResumeFit="hasAIResumeMatchPermission"
      :showJoinDate="false"
      :appliedJob="application.job.i18nName"
      :appliedJobCompany="application.job.company"
      :isSelected="application.selected"
      :isNew="!application.reviewed"
      :isEmployee="application.candidate.is_employee"
      :isAlumni="application.candidate.is_alumni"
      :isAgencyUploadedAndHid="application.is_agency_uploaded_and_hid"
      :appliedDate="application.apply_time"
      :channel="application.channel && application.channel.i18nName"
      :status="application.status.i18nName"
      :candidateId="application.candidate.id"
      :candidateName="application.candidate.name"
      :avatar="application.candidate.avatar"
      :gender="application.candidate.gender"
      :referralType="application.referral_type"
      :location="getLocationInfo(application.candidate.location)"
      :maxDegree="application.candidate.maxDegreeI18nName"
      :age="application.candidate.age"
      :email="application.candidate.email"
      :phone="application.candidate.mobile || application.candidate.second_phone"
      :duplicateCandidateList="application.duplicate_candidate_list"
      :workList="application.resume.work_list"
      :internList="application.resume.intern_list"
      :educationList="application.resume.education_list"
      :personalityFinished="application.candidate.personality_finished"
      :problemSolvingFinished="application.candidate.problem_solving_finished"
      :cultureFit="application.resume.culture_fit"
      :personalityFit="application.resume.personality_fit"
      :problemFit="application.resume.problem_fit"
      :resumeFit="application.ai_match_info?.resume_ai_match_score"
      :aiResumeMatchSummary="getResumeAIMatchSummary(application)"
      :operationList="operationList"
      :aiInterviewFit="application.ai_interview_info?.total_score"
      :showAiInterviewFit="showAiInterviewFit"
      :tagList="application.tag_list"
      :copiedInfo="application.copied_info"
      :disableChangeButton="isDisabled"
      :tooltip="tooltip"
      :showAIInterviewScore="showAIInterviewScore"
      :aiInterviewScore="application.ai_interview_score"
      :showInterviewScore="showInterviewScore"
      :interviewRecommendation="application.hm_interview_recommendation"
      :interviewScore="application.hm_interview_total_rate"
      :interviewCompletion="application.hm_interview_completion"
      :interviewHmInfoList="application.hm_interview_info_list"
      @avatarClick="onCandidateAvatarClick(application)"
      @candidateNameClick="onCandidateNameClick(application)"
      @statusClick="onCandidateStatusClick(application)"
      @selectChange="onSelectChange(application, $event)"
      @handleOperation="handleOperation(application, $event)"
    ></CandidateInfoCard>
  </div>
  <div v-else class="no-data">{{ $t('common.label.noData') }}</div>
</template>

<script>
import listMixin from '../listMixin'
import CandidateInfoCard from '../components/candidateInfoCard.vue'
import _ from 'lodash'
import { addI18nProperty, getConstantI18nValue, getI18nPropertyValue } from '@/i18n'
import { DEGREE } from '@/constant/meta'

export default {
  mixins: [listMixin],
  components: {
    CandidateInfoCard
  },
  data () {
    return {
      applicationList: [],
      applicationIds: []
    }
  },
  computed: {
    listEl () {
      return this.$el
    },
    selectedList () {
      return _.filter(this.applicationList, application => application.selected)
    },
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    }
  },
  created () {

  },
  methods: {
    getResumeAIMatchSummary (application) {
      if (!application.ai_match_info) {
        return ''
      }
      return getI18nPropertyValue(application.ai_match_info, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    },
    handleOperation (application, operation) {
      operation.operator(application)
    },
    refreshListData (data, oldData) {
      if (data !== oldData) {
        this.applicationIds = []
        this.applicationList = []
      }
      _.forEach(data, application => {
        if (this.applicationIds.includes(application.id)) {
          return
        }
        this.applicationIds.push(application.id)
        if (!application.job.i18nName) {
          addI18nProperty(application.job, 'i18nName', 'cn_title', 'title')
        }
        if (application.channel && !application.channel.i18nName) {
          addI18nProperty(application.channel, 'i18nName', 'cn_name', 'name')
        }
        if (!application.status.i18nName) {
          addI18nProperty(application.status, 'i18nName', 'name_cn', 'name_en')
        }
        application.candidate.avatar = application.is_agency_uploaded_and_hid ? this.$DjangoStaticUrl('images_en/default.jpg') : this.$DjangoBaseUrl(application.candidate.photo)
        application.candidate.email = application.is_agency_uploaded_and_hid ? '***' : application.candidate.email
        application.candidate.mobile = application.is_agency_uploaded_and_hid ? '***' : application.candidate.mobile
        application.candidate.second_phone = application.is_agency_uploaded_and_hid ? '***' : application.candidate.second_phone
        _.forEach(application.resume.work_list, this.formatWorkInfo)
        _.forEach(application.resume.intern_list, this.formatWorkInfo)
        _.forEach(application.resume.education_list, this.formatEducationInfo)
        this.applicationList.push(application)
      })
    },
    formatWorkInfo (work) {
      work.company = work.company_name
      work.job = work.job_title
      work.startDate = work.start_date
      work.endDate = work.end_date
    },
    formatEducationInfo (education) {
      if (!education.degree) {
        Object.defineProperty(education, 'degree', {
          configurable: true,
          enumerable: true,
          get () {
            return getConstantI18nValue(DEGREE, education.degree_level, 'meta.DEGREE')
          }
        })
      }
      addI18nProperty(education, 'school', 'school_cn_name', 'school_en_name')
      addI18nProperty(education, 'major', 'major_cn_name', 'major_en_name')
      education.startDate = education.start_date
      education.endDate = education.end_date
    },
    getLocationInfo (location) {
      if (!location) {
        return
      }
      const city = location.city
      const province = location.province
      const country = location.country
      return getI18nPropertyValue(city || province || country, 'cn_name', 'name')
    },
    onCandidateStatusClick (application) {
      this.emitCandidateStatusClickEvent(application)
    },
    onCandidateNameClick (application) {
      this.emitOpenDetailEvent(application)
    },
    onCandidateAvatarClick (application) {
      this.emitOpenDetailEvent(application)
    },
    onSelectChange (application, selected) {
      application.selected = selected
    }
  },
  watch: {
    data: {
      immediate: true,
      handler (val, oldVal) {
        this.refreshListData(val, oldVal)
      }
    },
    selectedList: {
      handler () {
        this.emitSelectedChangeEvent(this.selectedList)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-info-card-list {
  height: 100%;
  padding-top: 10px;
  overflow: auto;

  .candidate-info-card {
    margin-top: 14px;

    &:first-child {
      margin-top: 0;
    }
  }
}
.no-data {
  line-height: 60px;
  width: 50%;
  color: #909399;
  position: absolute;
  top: 50%;
  left: 50%;
}
</style>
